import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';

/**
 * Base de evidencia científica (§8 del encargo).
 *
 * QUÉ ES ESTO Y QUÉ NO ES
 *
 * No es «la IA sabe cosas». El §8 empieza diciendo que «la instrucción *utiliza
 * la evidencia más reciente* no sustituye una infraestructura de consulta», y
 * esto es esa infraestructura: fichas con identificador verificable, el registro
 * de las búsquedas que las produjeron, y un estado que dice si una ficha se
 * puede usar todavía.
 *
 * TRES COSAS QUE ESTE MODELO SEPARA A PROPÓSITO, porque el §8 las separa:
 *
 * 1. **Certeza científica** (`certainty`): cuánto respalda la literatura una
 *    afirmación.
 * 2. **Tipo de razonamiento** (`kind`): si lo que hay detrás es evidencia o una
 *    heurística práctica. «Una heurística práctica no es simplemente evidencia
 *    de baja calidad: debe identificarse como heurística», y por eso es un eje
 *    distinto y no el escalón más bajo del anterior.
 * 3. **Aplicabilidad individual** (`applicability`): cuánto se parece la
 *    población estudiada a quien usa esto.
 *
 * Fundirlas en un solo número —«confianza: 0,7»— es lo que convierte un
 * metaanálisis en gente entrenada en lo mismo que una corazonada bien
 * redactada.
 *
 * NADA DE ESTO SE SIEMBRA CON EJEMPLOS. La base nace vacía y lo dice. Una ficha
 * de muestra sería una referencia inventada, que es justo lo que el §8 prohíbe
 * en su última línea: «no inventes referencias ni detalles que no se hayan
 * podido consultar».
 */

// --------------------------------------------------------------------------
// Vocabulario
// --------------------------------------------------------------------------

/**
 * Qué clase de documento es.
 *
 * **El tipo NO asigna calidad por sí solo**, y el §8 lo dice con todas las
 * letras: «no asignes calidad únicamente por el tipo de publicación». Un
 * metaanálisis de seis estudios malos no vale más que un ensayo controlado
 * largo y bien hecho. Sirve para otra cosa: para saber QUÉ PREGUNTA puede
 * contestar cada documento, y para no confundir una revisión narrativa con una
 * sistemática.
 */
export const sourceTypeSchema = z.enum([
  'systematic_review',
  'meta_analysis',
  'randomized_trial',
  'longitudinal_study',
  'crossover_trial',
  'consensus_statement',
  'narrative_review',
  'preprint',
  'commentary',
]);
export type SourceType = z.infer<typeof sourceTypeSchema>;

/**
 * Evidencia o heurística: el eje que el §8 exige no confundir con la certeza.
 *
 * `heuristic` es una regla práctica que funciona y que nadie ha medido: «si un
 * ejercicio te deja la articulación dolorida dos días, cámbialo». No es
 * evidencia floja; es otra cosa, y presentarla como evidencia de certeza baja
 * sugiere que un estudio la respalda mal cuando lo que pasa es que no hay
 * ninguno.
 */
export const evidenceKindSchema = z.enum(['evidence', 'heuristic']);
export type EvidenceKind = z.infer<typeof evidenceKindSchema>;

/** Certeza CIENTÍFICA de lo que la fuente sostiene. Nada que ver con el atleta. */
export const certaintySchema = z.enum(['high', 'moderate', 'low', 'very_low']);
export type Certainty = z.infer<typeof certaintySchema>;

/**
 * Cuánto se parece la población estudiada a un culturista natural entrenado.
 *
 * Eje separado de la certeza porque lo son: un metaanálisis impecable en
 * personas desentrenadas de 70 años tiene certeza alta y aplicabilidad baja, y
 * confundirlos es como se justifica cualquier cosa con «hay un metaanálisis».
 */
export const applicabilitySchema = z.enum(['direct', 'indirect', 'speculative']);
export type Applicability = z.infer<typeof applicabilitySchema>;

/**
 * Horizonte de lo que la fuente midió.
 *
 * `acute` es una respuesta medida en horas o en una sesión —activación
 * electromiográfica, síntesis proteica postentreno, un pico hormonal—.
 * `chronic` es cambio sostenido a lo largo de semanas.
 *
 * Existe porque el §8 lo prohíbe expresamente: «no conviertas respuestas
 * agudas, medidas indirectas o hipótesis biomecánicas en demostraciones de
 * hipertrofia a largo plazo». Con este campo, una ficha aguda no puede
 * respaldar una afirmación sobre crecimiento sin que se vea.
 */
export const outcomeHorizonSchema = z.enum(['acute', 'chronic']);
export type OutcomeHorizon = z.infer<typeof outcomeHorizonSchema>;

/**
 * Qué se sabe del uso de sustancias en la muestra.
 *
 * `undocumented` es el valor honesto por defecto y el más frecuente: casi
 * ningún estudio lo verifica. El §8: «si el estatus de uso de sustancias de una
 * muestra no está documentado, no la describas como una muestra confirmada de
 * culturistas naturales».
 *
 * No es un detalle de pureza doctrinal. Los rangos de volumen y frecuencia que
 * tolera alguien con ayuda farmacológica no son los de alguien sin ella, y esta
 * aplicación es para lo segundo.
 */
export const substanceStatusSchema = z.enum([
  'documented_drug_free',
  'documented_enhanced',
  'mixed',
  'undocumented',
]);
export type SubstanceStatus = z.infer<typeof substanceStatusSchema>;

/**
 * Estado de la ficha en el ciclo del §8.
 *
 * El §8 separa «búsqueda y síntesis» de «evaluación y activación», y esto es
 * esa frontera hecha dato: una ficha recién recuperada nace `candidate` y NO la
 * usa ninguna programación hasta que se promueve.
 *
 * `retracted` no borra: el §8 pide «conservando trazabilidad de los planes que
 * las utilizaron», así que una fuente retirada se marca y se queda, y quien
 * mire un bloque viejo puede ver que se apoyó en algo que después se cayó.
 */
export const evidenceStatusSchema = z.enum(['candidate', 'active', 'superseded', 'retracted']);
export type EvidenceStatus = z.infer<typeof evidenceStatusSchema>;

// --------------------------------------------------------------------------
// La ficha
// --------------------------------------------------------------------------

/**
 * Una ficha de evidencia: un documento consultado y lo que se le puede pedir.
 *
 * `claims` son las afirmaciones que la ficha respalda, en el vocabulario del
 * dominio (`EVIDENCE_CLAIMS` en `domain/evidence/claims.ts`) y no en texto
 * libre. Esa es la clave de que una cita se pueda VALIDAR: el motor pregunta
 * «¿qué respalda subir el volumen del pectoral?» y la respuesta es un conjunto
 * de identificadores, no un párrafo.
 */
export const evidenceSourceSchema = syncColumnsSchema.extend({
  // --- identificación verificable ----------------------------------------
  title: z.string().min(5).max(300),
  /** Tal y como figuran. Sin normalizar: normalizar nombres es inventar. */
  authors: z.string().min(2).max(300),
  publication: z.string().min(2).max(200),
  year: z.number().int().min(1950).max(2100),
  /**
   * Al menos UNO de los tres tiene que existir, y lo impone
   * `validateEvidenceSource` en el dominio y no este esquema: una ficha sin
   * ninguna forma de localizar el documento no es una ficha, es una
   * afirmación, y el §8 exige «identificador verificable».
   */
  doi: z.string().max(120).nullable(),
  pmid: z.string().max(20).nullable(),
  url: z.string().max(500).nullable(),

  // --- qué clase de cosa es ----------------------------------------------
  sourceType: sourceTypeSchema,
  kind: evidenceKindSchema,
  /**
   * Revisado por pares. Se guarda aparte de `sourceType` porque son
   * independientes: hay prepublicaciones de ensayos y revisiones narrativas
   * publicadas en revistas con revisión.
   */
  peerReviewed: z.boolean(),

  // --- lo que estudió -----------------------------------------------------
  population: z.string().min(5).max(400),
  substanceStatus: substanceStatusSchema,
  intervention: z.string().min(5).max(400),
  /** Con qué se comparó. `null` cuando el diseño no tiene comparador. */
  comparison: z.string().max(400).nullable(),
  outcomeHorizon: outcomeHorizonSchema,
  /** Semanas que duró. `null` si no aplica o no se pudo leer. */
  durationWeeks: z.number().int().min(0).max(520).nullable(),
  findings: z.string().min(10).max(1200),
  limitations: z.string().min(10).max(1200),

  // --- los tres ejes que no se funden -------------------------------------
  certainty: certaintySchema,
  applicability: applicabilitySchema,

  // --- para qué sirve aquí ------------------------------------------------
  claims: z.array(z.string().min(2).max(60)).min(1).max(12),

  // --- procedencia y vigencia ---------------------------------------------
  /**
   * Si se llegó al TEXTO COMPLETO o solo al resumen.
   *
   * El §8 lo pide entre los datos de la búsqueda, y se guarda también en la
   * ficha porque es lo que califica todo lo demás: `limitations` sacadas de un
   * abstract son las que el abstract quiso contar.
   */
  fullTextAccess: z.boolean(),
  /**
   * Fecha hasta la que el DOCUMENTO revisó la literatura, no cuándo se publicó.
   *
   * El §8 exige distinguirlas y la diferencia es de años: una revisión
   * sistemática publicada en 2026 puede haber cerrado su búsqueda en 2023. Usar
   * la fecha de publicación para decir «evidencia reciente» es exactamente el
   * error que este campo evita. `null` cuando el tipo de documento no revisa
   * literatura (un ensayo) o cuando no consta.
   */
  literatureThrough: localDateSchema.nullable(),
  /** Cuándo se revisó ESTA ficha por última vez. */
  reviewedOn: localDateSchema,
  status: evidenceStatusSchema,
  /** Sube cada vez que se reescribe la ficha sobre el mismo documento. */
  version: z.number().int().min(1).max(999),
  /** La búsqueda que la trajo. Sin esto una ficha no tiene procedencia. */
  searchId: z.uuid().nullable(),
  /** Por qué se promovió, se superó o se retiró. Se enseña y se conserva. */
  statusNote: z.string().max(400).nullable(),
});

export type EvidenceSource = z.infer<typeof evidenceSourceSchema>;

// --------------------------------------------------------------------------
// El registro de la búsqueda
// --------------------------------------------------------------------------

/**
 * Una búsqueda, con todo lo que el §8 pide guardar de ella.
 *
 * POR QUÉ SE GUARDA LA BÚSQUEDA Y NO SOLO LO ENCONTRADO
 *
 * Porque lo que NO se encontró también informa. Una pregunta buscada en tres
 * sitios que no devuelve nada aplicable es un resultado —«no hay evidencia
 * directa»— y sin registro es indistinguible de no haber buscado. El §8 pide
 * expresamente las «limitaciones de la búsqueda».
 */
export const evidenceSearchSchema = syncColumnsSchema.extend({
  /** La pregunta, en lenguaje natural y en vocabulario del dominio. */
  question: z.string().min(10).max(300),
  /** Los `claims` que esta búsqueda pretendía respaldar o refutar. */
  claims: z.array(z.string().min(2).max(60)).min(1).max(12),
  /** Lo que de verdad se escribió en el buscador. */
  queries: z.array(z.string().min(2).max(300)).min(1).max(20),
  searchedOn: localDateSchema,
  /** Periodo cubierto por la búsqueda, si se acotó. */
  coversFrom: localDateSchema.nullable(),
  coversTo: localDateSchema.nullable(),
  /** Criterios de selección aplicados, en prosa breve. */
  selectionCriteria: z.string().min(10).max(800),
  /** Cuántos documentos devolvió y cuántos se quedaron. */
  retrievedCount: z.number().int().min(0).max(500),
  keptCount: z.number().int().min(0).max(500),
  /**
   * Lo que esta búsqueda NO pudo hacer: bases sin acceso, muros de pago,
   * idiomas, ventana temporal. Obligatorio y con mínimo: «ninguna» es una
   * respuesta válida pero hay que escribirla, porque una limitación en blanco
   * se lee como «no las hay» cuando casi siempre significa «no se miró».
   */
  limitations: z.string().min(5).max(800),
  /** Proveedor y versión del prompt, para poder reproducir. */
  provider: z.string().min(1).max(60),
  promptVersion: z.string().min(1).max(40),
});

export type EvidenceSearch = z.infer<typeof evidenceSearchSchema>;

// --------------------------------------------------------------------------
// Frecuencia de revisión
// --------------------------------------------------------------------------

/**
 * Cada cuánto se revisa la base.
 *
 * **Es una decisión de producto, no un umbral científico**, y el §8 insiste en
 * que se diga así. La literatura no cambia el día 30; lo que cambia es cuánto
 * está uno dispuesto a ir por detrás.
 *
 * Treinta días como punto de partida operativo, que es lo que el §8 sugiere, y
 * una comprobación antes de un bloque nuevo si la revisión está vencida.
 * Configurable: vive aquí como valor por defecto, no como ley.
 */
export const EVIDENCE_REVIEW = {
  /** Días tras los cuales la base se considera vencida. */
  intervalDays: 30,
  /**
   * Días de margen antes de avisar. Con 7, un bloque que empieza el día 31 no
   * se para: se avisa el 23 y hay una semana para revisar sin prisa.
   */
  warnBeforeDays: 7,
} as const;
